!function () {
  'use strict';

  /*
   * Tabs controller
   *
   * Any product block carrying [data-buy-panel] is a tab panel candidate
   * ('buy-save' and 'complete-routine' blocks). When two or more panels share
   * the same parent, a tab bar is injected before the first one and the
   * panels become tab content. A single panel renders untouched, so the two
   * blocks stay fully independent.
   */
  function initBuyTabs(scope) {
    const panels = [...(scope || document).querySelectorAll('[data-buy-panel]')];
    const groups = new Map();

    panels.forEach((panel) => {
      const parent = panel.parentElement;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(panel);
    });

    groups.forEach((groupPanels, parent) => {
      const existingBar = parent.querySelector('.buy-tabs');

      if (groupPanels.length < 2) {
        // Panel removed in the editor - clean up a leftover tab bar
        if (existingBar) existingBar.remove();
        groupPanels.forEach((panel) => panel.classList.remove('buy-tab-hidden', 'is-tabbed'));
        return;
      }

      groupPanels.forEach((panel) => panel.classList.add('is-tabbed'));

      if (existingBar) existingBar.remove();

      const bar = document.createElement('div');
      bar.className = 'buy-tabs';
      bar.setAttribute('role', 'tablist');

      const tabsBg = groupPanels.map((panel) => panel.dataset.tabsBg).find((color) => color);
      if (tabsBg) bar.style.setProperty('--buy-tabs-bg', tabsBg);

      const activate = (index) => {
        groupPanels.forEach((panel, i) => {
          panel.classList.toggle('buy-tab-hidden', i !== index);
        });
        bar.querySelectorAll('.buy-tabs__tab').forEach((tab, i) => {
          tab.classList.toggle('is-active', i === index);
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
      };

      groupPanels.forEach((panel, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'buy-tabs__tab';
        tab.setAttribute('role', 'tab');
        tab.textContent = panel.dataset.tabLabel || '';
        tab.addEventListener('click', () => activate(index));
        panel.buyTabActivate = () => activate(index);
        bar.appendChild(tab);
      });

      parent.insertBefore(bar, groupPanels[0]);
      activate(0);
    });
  }

  if (!window.__buyTabsInit) {
    window.__buyTabsInit = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initBuyTabs());
    } else {
      initBuyTabs();
    }

    // Theme editor: re-init after section re-renders, and reveal a hidden
    // panel when the merchant selects its block
    document.addEventListener('shopify:section:load', (event) => initBuyTabs(event.target));
    document.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest('[data-buy-panel]');
      if (panel && panel.classList.contains('buy-tab-hidden') && panel.buyTabActivate) {
        panel.buyTabActivate();
      }
    });
  }

  /*
   * Buy & save variant cards
   */
  if (customElements.get('buy-save')) return;

  customElements.define(
    'buy-save',
    class BuySave extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-buy-save-add]');
        this.buttonPrice = this.querySelector('[data-atc-total]');
        this.errorContainer = this.querySelector('[data-cart-errors-container]');

        this.addEventListener('change', this.onChange.bind(this));
        this.button?.addEventListener('click', this.onSubmit.bind(this));

        this.updateFromSelection();
      }

      get checkedInput() {
        return this.querySelector('input[type="radio"]:checked');
      }

      onChange(event) {
        if (event.target.type !== 'radio') return;
        this.updateFromSelection();
      }

      updateFromSelection() {
        const input = this.checkedInput;

        // Sync card styling from the real input state - also covers the
        // browser restoring a different selection on back/forward navigation
        this.querySelectorAll('[data-buy-option]').forEach((option) => {
          option.classList.toggle('is-selected', option.querySelector('input').checked);
        });

        if (this.buttonPrice) {
          this.buttonPrice.innerHTML = input ? `&bull; ${window.theme.formatMoney(parseInt(input.dataset.price, 10), theme.moneyFormat)}` : '';
        }

        if (this.button && !this.button.classList.contains('is-loading')) {
          this.button.disabled = !input;
        }
      }

      onSubmit() {
        const input = this.checkedInput;
        if (!input) return;

        this.button.classList.add('is-loading');
        this.button.disabled = true;
        this.hideError();

        fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify({items: [{id: parseInt(input.value, 10), quantity: 1}]}),
        })
          .then((response) => response.json())
          .then((response) => {
            this.button.classList.remove('is-loading');

            if (response.status) {
              this.button.disabled = false;
              this.showError(response);
              return;
            }

            this.button.classList.add('is-added');

            setTimeout(() => {
              this.button.classList.remove('is-added');
              this.button.disabled = false;
            }, 2000);

            publish(theme.PUB_SUB_EVENTS.cartUpdate, {
              source: 'buy-save',
              cartData: response,
            });

            // MonsterCart (app embed) opens its own drawer on add, so the theme's
            // cart-page redirect would yank the shopper straight back out of it.
            // Probed at add time, not on upgrade: the app embed injects its
            // script tag independently of when this element connects.
            const monsterActive = !!document.querySelector('script[src*="/cart_renderer"]');

            if (theme.settings.cartType === 'page' && !monsterActive) {
              window.location = theme.routes.cart_url;
              return;
            }

            // Hand the new line to the app so its drawer re-reads the cart.
            // Bare name, not window.: covers the global being a top-level
            // const in the app bundle rather than a window property, and
            // guards the bundle still being in flight on a very early add.
            if (monsterActive && typeof monster_refresh === 'function') monster_refresh();

            // GWP/BOGO extension re-processes the cart and dispatches theme:cart:refresh itself
            const isGwpActive = window.GWP_CART_DRAWER_CONFIG || window.BUY_X_GET_Y_CART_DRAWER_CONFIG;

            if (isGwpActive) {
              document.dispatchEvent(
                new CustomEvent('theme:cart:change', {
                  bubbles: true,
                  detail: {source: 'buy-save'},
                })
              );
            } else {
              document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
            }
          })
          .catch((error) => {
            this.button.classList.remove('is-loading');
            this.button.disabled = false;
            this.showError(error);
          });
      }

      showError(data) {
        if (!this.errorContainer) return;

        let errorMessage = data?.message || window.theme.strings?.unavailable || 'Error';

        if (data?.description && data.description !== data.message) {
          errorMessage = `${data.message}: ${data.description}`;
        }

        this.errorContainer.innerHTML = `<div class="errors">${errorMessage}<button type="button" class="errors__close" data-close-error><svg aria-hidden="true" focusable="false" role="presentation" width="24px" height="24px" stroke-width="1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor" class="icon icon-cancel"><path d="M6.758 17.243L12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path></svg></button></div>`;
        this.errorContainer.classList.add('is-visible');

        this.errorContainer.querySelector('[data-close-error]')?.addEventListener('click', () => this.hideError(), {once: true});
      }

      hideError() {
        if (!this.errorContainer) return;
        this.errorContainer.classList.remove('is-visible');
        this.errorContainer.innerHTML = '';
      }
    }
  );
}();
